import { useCallback, useEffect, useMemo } from 'react';

import {
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Input,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useListFetcher } from '../../../hooks/useListFetcher';
import { useSyncedSearch } from '../../../hooks/useSyncedSearch';
import { useTableUrlSync } from '../../../hooks/useTableUrlSync';
import { useUrlHydration } from '../../../hooks/useUrlHydration';
import { ClientModel } from '../../../models/client';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchClientsWithParams,
  hydrateFromUrl,
  searchClientsByNameWithParams,
  setClientSearchQuery,
} from '../../../store/features/clients/clientsSlice';
import { ListApiConfig, SortFieldMap } from '../../../types/list';
import { formatDate, UserPermissions } from '../../../util';
import { buildListParams } from '../../../util/listApiParams';
import { parseUrlParams } from '../../../util/listUrl';
import { DrawerHashRoute } from '../../containers/Drawers/types';
import NoPermission from '../../elements/NoPermission';
import NoResultsFound from '../../elements/NoResultsFound';
import ViewWrapper from '../../elements/ViewWrapper';
import './ClientsView.less';

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

const { Title } = Typography;

export interface ClientModelWithExtensions extends ClientModel {
  markets: string;
  client_type: string;
}

const ClientsView = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const listApiConfig: ListApiConfig = useMemo(
    () => ({
      defaultSort: 'created_at:desc',
      searchParamKey: 'name',
      searchTransformer: (s) => `*${s.toLowerCase()}*`,
      columns: 'license.*,*',
    }),
    []
  );

  const { clients, fetchingClients, searchingClients, pagination, error } =
    useAppSelector((state) => state.clients);
  const {
    page,
    pageSize,
    searchQuery: storeSearch,
    sort,
  } = useAppSelector((state) => state.clients);

  const loading = fetchingClients || searchingClients;

  const hasNoSearchResultsFound = useMemo(
    () => !loading && !!storeSearch && clients.length === 0,
    [clients.length, loading, storeSearch]
  );

  const hasClients = loading || (clients && clients.length > 0);

  const SORT_FIELD_MAP: SortFieldMap = useMemo(
    () => ({
      name: 'name',
      created_at: 'created_at',
      updated_at: 'updated_at',
      status: 'status',
    }),
    []
  );

  const { currentSortField, currentSortOrder, onTableChange } = useTableUrlSync(
    {
      location,
      history,
      page,
      pageSize,
      sortFieldMap: SORT_FIELD_MAP,
    }
  );

  const headerTitle = useMemo(
    (): JSX.Element => (
      <Space direction="horizontal">
        <Title level={3}>Clients</Title>
        <Badge
          size="default"
          count={pagination?.count || 0}
          className="total-count-badge"
        />
      </Space>
    ),
    [pagination?.count]
  );

  const columns: ColumnsType<ClientModel> = [
    {
      title: 'Name',
      width: '35%',
      dataIndex: 'name',
      sorter: true,
      sortOrder: currentSortField === 'name' ? currentSortOrder : null,
      render: (name: string | undefined, client: ClientModel) => (
        <Link className="ant-btn-link" to={`/clients/${client?.uuid}`}>
          {name || 'N/A'}
        </Link>
      ),
    },
    {
      title: 'Last updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      // width: 140,
      render: (date: string) => {
        return <div>{date ? formatDate(date) : null}</div>;
      },
      sorter: true,
      sortOrder: currentSortField === 'updated_at' ? currentSortOrder : null,
    },
    {
      title: 'Studio Key?',
      key: 'studio_license_key',
      width: 100,
      align: 'center',
      render: (_: unknown, client: ClientModel) => {
        const hasKey =
          client?.studio_license_key != null &&
          client.studio_license_key !== '' &&
          String(client.studio_license_key).trim() !== '';
        return hasKey ? (
          <CheckOutlined style={{ color: '#52c41a', fontSize: 16 }} />
        ) : (
          <CloseOutlined style={{ color: 'rgba(0,0,0,0.25)', fontSize: 16 }} />
        );
      },
    },
    {
      title: 'Action',
      width: 180,
      align: 'center',
      render: (client: ClientModel) => {
        return (
          <Space direction="horizontal" size="middle">
            {hasPermission(UserPermissions.ClientsEdit) && (
              <Tooltip title="Edit">
                <Button
                  type="link"
                  onClick={() =>
                    history.push({
                      hash: DrawerHashRoute.ClientForm,
                      state: { client },
                    })
                  }
                >
                  Edit
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const { hasHydrated } = useUrlHydration({
    location,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    onHydrate: (urlControls) => dispatch(hydrateFromUrl(urlControls)),
  });

  const fetchClientsThunk = useCallback(
    (params: Record<string, any>) =>
      dispatch(fetchClientsWithParams({ params })),
    [dispatch]
  );

  useListFetcher({
    hasHydrated,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    config: listApiConfig,
    fetchThunk: fetchClientsThunk,
  });

  // Defensive fetch using URL params after hydration (prevents stale results on Back with search)
  useEffect(() => {
    if (!hasHydrated) return;
    const paramsFromUrl = parseUrlParams(location.search);
    const urlPage = Number(paramsFromUrl.page) || page;
    const urlPageSize = Number(paramsFromUrl.pageSize) || pageSize;
    const urlSearch = paramsFromUrl.search ?? storeSearch;
    const urlSort = paramsFromUrl.sort ?? sort;
    const params = buildListParams(
      {
        page: urlPage,
        pageSize: urlPageSize,
        search: urlSearch,
        sort: urlSort,
      },
      listApiConfig
    );
    dispatch(fetchClientsWithParams({ params }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, location.search]);

  const onSearch = useCallback(
    (value: string) => {
      const params = buildListParams(
        { page: 1, pageSize, search: value, sort },
        listApiConfig
      );
      return dispatch(searchClientsByNameWithParams({ params }));
    },
    [dispatch, pageSize, sort, listApiConfig]
  );

  const { searchValue, onChange, clear } = useSyncedSearch({
    storeSearchValue: storeSearch,
    setStoreSearch: (value) => dispatch(setClientSearchQuery(value)),
    pageSize,
    sort,
    history,
    location,
    onSearch,
  });

  const headerSectionRight = useMemo(
    (): JSX.Element => (
      <Space direction="horizontal" size="middle">
        <Input
          className="clients-view__search-input"
          allowClear={!searchingClients}
          value={searchValue}
          onChange={(e) => onChange(e.target.value)}
          onPressEnter={(e: any) => void onSearch(e.target.value)}
          placeholder="Search clients..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
          suffix={loading && <Spin indicator={antSpinIcon} />}
        />
        {hasPermission(UserPermissions.ClientsCreate) && (
          <Link to={DrawerHashRoute.ClientForm}>
            <Button type="primary">
              Create New Client
              <PlusOutlined />
            </Button>
          </Link>
        )}
      </Space>
    ),
    [hasPermission, loading, onChange, onSearch, searchValue, searchingClients]
  );

  // pagination handled by useTableUrlSync -> onTableChange
  const paginationSetting = {
    hideOnSinglePage: false,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total: number, range: number[]) =>
      `${range[0]}-${range[1]} of ${total} items`,
    style: {
      marginTop: '0px',
      padding: '2rem 1rem',
      background: 'white',
      borderRadius: '0 0 4px 4px',
    },
    total: pagination?.count || 0,
    current: page,
    pageSize,
  };

  if (!hasPermission(UserPermissions.ClientsView)) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={headerTitle}
      headerSectionRight={headerSectionRight}
    >
      <div className="clients-view__content">
        {error.message && (
          <div className="error-message">Error: {error.message}</div>
        )}
        {hasClients && hasNoSearchResultsFound ? (
          <NoResultsFound searchQuery={searchValue} onClear={clear} />
        ) : (
          <Table
            sortDirections={['ascend', 'descend', 'ascend']}
            columns={columns}
            rowKey={(client: ClientModel) => `${client.uuid}-row-key`}
            dataSource={clients}
            pagination={paginationSetting}
            loading={loading}
            onChange={onTableChange}
            className="clients-table-list"
          />
        )}
      </div>
    </ViewWrapper>
  );
};

export default ClientsView;
